<template>
  <div class="market">
    <div class="container">
      <div class="market__wrapper">
        <div class="market__top">
          <div class="market__top-block">{{ tagFromParams === 'top' ? t('home.tag.top.title2') : t('home.tag.best.title2') }}</div>
          <div class="market__top-buttons">
            <div class="market__top-button" @click="setNameOrdering">
              <p>{{ t('market.sortBy') }} <span>{{ t('market.name') }}</span></p>
              <span class="market__top-button-inner">
                <span
                    :class="orderBy === 'name' ? 'market__top-button-small' : 'market__top-button-active'"
                />
                <span />
                <span
                    :class="orderBy === 'name' ? 'market__top-button-active' : 'market__top-button-small'"
                />
              </span>
            </div>
            <div class="market__top-button" @click="setPriceOrdering">
              <p>{{ t('market.sortBy') }} <span>{{ t('market.price') }}</span></p>
              <span class="market__top-button-inner">
                <span
                    :class="orderBy === 'price' ? 'market__top-button-small' : 'market__top-button-active'"
                />
                <span />
                <span
                    :class="orderBy === 'price' ? 'market__top-button-active' : 'market__top-button-small'"
                />
              </span>
            </div>
          </div>
        </div>
        <div class="market__main">
          <div class="market__grid" v-if="products.length > 0">
            <product-card
                v-for="product in products"
                :key="product.node.uuid"
                :product="product.node"
            />
            <div class="market__grid-observer" ref="observer"></div>
          </div>
          <div class="market__grid" v-else-if="isLoading">
            <product-card-skeleton v-for="idx in 12" :key="idx" />
          </div>
          <p v-else class="market__empty">{{ t('market.empty') }}</p>
        </div>
      </div>
    </div>
    <transition name="opacity" mode="out-in">
      <product-view
          v-if="activeViewProduct"
          :product="activeViewProduct"
      />
    </transition>
  </div>
</template>

<script setup>
import {computed, onMounted, ref, watch} from "vue";
import {useI18n} from "vue-i18n";
import {useRoute} from "vue-router";
import {useIntersectionObserver} from "@vueuse/core";
import {Products} from "@/core/api/products.js";
import ProductCard from "@/components/cards/product-card.vue";
import ProductCardSkeleton from "@/components/skeletons/product-card-skeleton.vue";
import ProductView from "@/components/cards/product-view.vue";
import {useAppStore} from "@/stores/app.js";

const {t} = useI18n()
const route = useRoute()
const appStore = useAppStore()

const tagFromParams = computed(() => {
  return route.params.tag ? route.params.tag : null
})

const isLoading = ref(false)
const observer = ref(null)

const { stop } = useIntersectionObserver(
    observer,
    ([{ isIntersecting }], observerElement) => {
      if (isIntersecting && pageInfo.value.hasNextPage) {
        getMoreProducts()
      }
    },
)

const products = ref([])
const pageInfo = ref([])
const cursorForAfter = ref('0')
const orderBy = ref(route.query['orderBy'] ? String(route.query['orderBy']) : '')

const query = computed(() => {
  return `?tag=${tagFromParams.value}&orderBy=${orderBy.value}`
})

const toggleOrdering = (type) => {
  const currentType = orderBy.value.startsWith('-') ? orderBy.value.slice(1) : orderBy.value;
  orderBy.value = (currentType === type) ? (orderBy.value.startsWith('-') ? type : `-${type}`) : type;
};
const setPriceOrdering = () => toggleOrdering('price');
const setNameOrdering = () => toggleOrdering('name');

const getProducts = async () => {
  isLoading.value = true
  cursorForAfter.value = '0'

  const res = await Products.getProducts(
      `after: "${cursorForAfter.value}"` +
      `, first: 20` +
      `, tags: "${tagFromParams.value}"` +
      `, orderBy: "${orderBy.value}"`
  )
  products.value = res.data?.products?.edges
  pageInfo.value = res.data?.products?.pageInfo

  isLoading.value = false
}

const getMoreProducts = async () => {
  isLoading.value = true

  cursorForAfter.value = pageInfo.value.endCursor

  const res = await Products.getProducts(
      `after: "${cursorForAfter.value}"` +
      `, first: 20` +
      `, tags: "${tagFromParams.value}"` +
      `, orderBy: "${orderBy.value}"`
  )
  products.value.push(...res.data.products.edges)
  pageInfo.value = res.data.products.pageInfo

  isLoading.value = false
}

watch(
    () => query.value,
    async () => {
      products.value = [];
      history.pushState({}, '', route.path + query.value);
      await getProducts();
    }
)

const activeViewProduct = computed(() => {
  return appStore.activeViewProduct
})

onMounted( async () => {
  await getProducts()
})
</script>

<style lang="scss" scoped>
.market {
  padding-top: 170px;
  position: relative;

  &__top {
    display: flex;
    align-items: center;
    gap: 30px;
    padding-bottom: 30px;
    border-bottom: 1px solid #EDEDED;

    &-block {
      border-radius: 10px;
      background-color: #003a09;
      padding: 10px 50px;

      text-transform: capitalize;
      color: $white;
      text-align: center;
      font-size: 20px;
      font-weight: 600;
      line-height: 25.801px;
    }

    &-buttons {
      display: flex;
      align-items: center;
      gap: 15px;
    }

    &-button {
      cursor: pointer;
      border-radius: 10px;
      background-color: #F3F9FB;
      display: flex;
      align-items: center;
      gap: 20px;
      padding: 11px 15px;
      transition: 0.2s;

      @include hover {
        box-shadow: 0 0 10px rgba(0, 0, 0, 0.2);
      }

      & p {
        color: #01AC1B;
        font-size: 14px;
        font-weight: 400;
        line-height: 18px;

        & span {
          font-weight: 700;
        }
      }

      &-inner {
        display: flex;
        align-items: start;
        flex-direction: column;
        justify-content: space-between;
        width: 40px;
        height: 23px;

        & span {
          height: 2px;
          background-color: #01AC1B;
          border-radius: 0.5rem;
          width: 75%;
          transition: all 0.3s ease;
        }
      }

      &-small {
        width: 50% !important;
      }

      &-active {
        width: 100% !important;
      }
    }
  }

  &__main {
    margin-top: 50px;
  }

  &__grid {
    width: 100%;
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(225px, 225px));
    column-gap: 20px;
    row-gap: 30px;
    justify-content: space-between;

    &-observer {
      width: 100%;
      height: 10px;
      background-color: transparent;
    }
  }

  &__empty {
    text-align: center;
    font-size: 20px;
    font-weight: 600;
    line-height: 26px;
    color: #212844;
  }
}

@media (max-width: 700px) {
  .market {
    &__top {
      flex-direction: column;
      align-items: flex-start;
      gap: 15px;
    }
  }
}

@media (max-width: 550px) {
  .market {
    &__top {
      &-buttons {
        flex-direction: column;
      }
    }

    &__grid {
      justify-content: center;
    }
  }
}
</style>